import React from 'react';
import { Col, Row, Space, Typography, Card, Divider, Tag } from "antd";
import { t } from "i18next";
import moment from "moment";
import { MdCancel, MdPerson, MdDateRange, MdAccessTime, MdLocalHospital, MdOutlineDescription } from "react-icons/md";
import { BsFillCheckCircleFill, BsGenderMale, BsGenderFemale, BsTelephone, BsEnvelope } from "react-icons/bs";
import Helper from "../../../core/Helper"; // Projenizdeki yol

const { Text, Title } = Typography;

export default function AppointmentPreview({ patient, appointment }) {

    // Yardımcı: İkonlu Bilgi Satırı
    const InfoRow = ({ icon, title, value, highlight = false }) => (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ marginTop: 4, marginRight: 8, color: '#1890ff' }}>
                {icon}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{title}</Text>
                <Text strong={highlight} style={{ fontSize: highlight ? 15 : 14 }}>
                    {value || '--'}
                </Text>
            </div>
        </div>
    );

    // Yardımcı: Durum Rengi ve Etiketi
    const renderStateTag = (state) => {
        if (state) {
            return (
                <Tag color={state.color} style={{ margin: 0, padding: '4px 10px', fontSize: 13 }}>
                    {state.name}
                </Tag>
            );
        }
        return <Tag>--</Tag>;
    };

    return (
        <div style={{ padding: 8 }}>
            <Row gutter={[16, 16]}>

                {/* --- SOL KART: HASTA BİLGİLERİ --- */}
                <Col xs={24} lg={10}>
                    <Card
                        bordered={false}
                        style={{ height: '100%', borderRadius: 12 }}
                        bodyStyle={{ padding: 24 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: '50%', backgroundColor: '#e6f7ff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1890ff', fontSize: 24, marginRight: 16
                            }}>
                                <MdPerson />
                            </div>
                            <div>
                                <Title level={4} style={{ margin: 0 }}>{patient.name} {patient.lastname}</Title>
                                <Text type="secondary">ID: {patient.customId} {(patient.otherPatientId ? (` - ${patient.otherPatientId}`) : '')}</Text>
                            </div>
                        </div>

                        <Divider style={{ margin: '12px 0 24px 0' }} />

                        <InfoRow
                            icon={patient.gender === 'Female' ? <BsGenderFemale /> : <BsGenderMale />}
                            title={t('Gender') + ' / ' + t('Birthdate')}
                            value={`${t(patient.gender)} • ${patient.birthDay ? `${patient.birthDay?.toString().padStart(2, '0')}/${patient.birthMonth?.toString().padStart(2, '0')}/${patient.birthYear}` : '--'}`}
                        />

                        <InfoRow
                            icon={<BsTelephone />}
                            title={t('Contact')}
                            value={
                                <Space direction="vertical" size={0}>
                                    <Text>{Helper.Phone.intlPhoneFormat(patient.phoneCode1, patient.phone1)}</Text>
                                    {patient.phone2 && (<Text type="secondary">{Helper.Phone.intlPhoneFormat(patient.phoneCode2, patient.phone2)}</Text>)}
                                </Space>
                            }
                        />

                        {patient.email && (
                            <InfoRow
                                icon={<BsEnvelope />}
                                title={t('Email')}
                                value={patient.email}
                            />
                        )}

                        <InfoRow
                            icon={<MdOutlineDescription />}
                            title={t('Patient Note')}
                            value={patient.note}
                        />
                    </Card>
                </Col>

                {/* --- SAĞ KART: RANDEVU DETAYLARI --- */}
                <Col xs={24} lg={14}>
                    <Card
                        bordered={false}
                        style={{ height: '100%', borderRadius: 12 }}
                        bodyStyle={{ padding: 24 }}
                    >
                        {/* Başlık Alanı */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'start', gap: 8, justifyContent: 'space-between', width: '100%', flexWrap: 'wrap' }}>
                                <Text strong style={{ fontSize: 24, color: '#1890ff', textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>
                                    {appointment.unit.name}
                                </Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <Title level={4} style={{ margin: 0, fontSize: 24, color: '#262626' }}>
                                        {appointment.institution.name}
                                    </Title>
                                    {appointment.appointmentType?.name && (
                                        <div style={{ marginTop: 4 }}>
                                            <Tag icon={<MdOutlineDescription />} style={{ margin: 0, padding: '2px 8px', fontSize: 12, borderRadius: 4, background: 'rgba(0,0,0,0.02)', border: '1px solid #d9d9d9' }}>
                                                {appointment.appointmentType?.name}
                                            </Tag>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Tarih ve Saat Kutusu (Vurgulu) */}
                        <div style={{
                            backgroundColor: '#f9f9f9', padding: 16, borderRadius: 8, border: '1px solid #eee',
                            display: 'flex', alignItems: 'start', gap: 16, marginBottom: 24, flexWrap: 'wrap'
                        }}>
                            <MdDateRange size={24} color="#595959" />
                            <div>
                                <Text strong style={{ display: 'block', fontSize: 16, lineHeight: '1.4' }}>
                                    {moment(new Date(appointment.startDate)).format('DD/MM/yyyy')}
                                </Text>
                                <Text type="secondary" style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
                                    {t(moment(appointment.startDate).format('dddd'))}
                                </Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1890ff', fontWeight: 600 }}>
                                    <MdAccessTime />
                                    {moment(new Date(appointment.startDate)).format('HH:mm')} - {moment(new Date(appointment.endDate)).format('HH:mm')}
                                </div>
                            </div>
                            {renderStateTag(appointment.state)}
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12}>
                                <InfoRow
                                    icon={<MdPerson />}
                                    title={t('Doctor')}
                                    value={appointment.doctor.name}
                                />
                            </Col>
                            {/* Appointment Type removed from here */}
                            <Col span={24} sm={12}>
                                <InfoRow
                                    icon={<MdOutlineDescription />}
                                    title={t('Anamnez') + ' / ' + t('Note')}
                                    value={
                                        <Space direction="vertical">
                                            {appointment.anamnez && <Text type="secondary">Anamnez: {appointment.anamnez}</Text>}
                                            {appointment.note && <Text>Not: {appointment.note}</Text>}
                                            {!appointment.anamnez && !appointment.note && '--'}
                                        </Space>
                                    }
                                />
                            </Col>
                        </Row>

                        <Divider style={{ margin: '12px 0' }} />

                        {/* Check-in Alanı */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Text type="secondary">{t('Check-in Status')}:</Text>

                            {!appointment.checkIn ? (
                                <Tag color="error" icon={<MdCancel style={{ position: 'relative', top: 1 }} />}>
                                    {t('Not Checked In')}
                                </Tag>
                            ) : (
                                <Tag color="success" icon={<BsFillCheckCircleFill style={{ position: 'relative', top: 1 }} />}>
                                    {t('Check-in')}: {appointment.checkInDate && moment(new Date(appointment.checkInDate)).format('HH:mm')}
                                </Tag>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div >
    );
}